import React, { useState, Fragment, useEffect, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { apiService } from "../../services/api";
import {
  Salesperson,
  CreateSalespersonData,
  SalespersonLoginInfo,
} from "../../types/api";
import toast from "react-hot-toast";

interface EditSalespersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  salesperson: Salesperson;
}

const EditSalespersonModal: React.FC<EditSalespersonModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  salesperson,
}) => {
  const [formData, setFormData] = useState<CreateSalespersonData>({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    employmentDate: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Login management state
  const [loginInfo, setLoginInfo] = useState<SalespersonLoginInfo | null>(null);
  const [loadingLoginInfo, setLoadingLoginInfo] = useState(false);
  const [showCreateLogin, setShowCreateLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [originalLoginEmail, setOriginalLoginEmail] = useState("");
  const [showDeleteLoginConfirm, setShowDeleteLoginConfirm] = useState(false);
  const [loginPasswordConfirm, setLoginPasswordConfirm] = useState("");
  const [loginUserType, setLoginUserType] = useState<"salesperson" | "manager">("salesperson");
  const [originalLoginUserType, setOriginalLoginUserType] = useState<"salesperson" | "manager">("salesperson");
  const [monthlyMinuteLimit, setMonthlyMinuteLimit] = useState<string>("");
  const [hasLimit, setHasLimit] = useState(false);

  // Initialize form data when salesperson changes
  useEffect(() => {
    console.log("=== EditSalespersonModal useEffect ===");
    console.log("Salesperson object:", salesperson);

    if (salesperson) {
      console.log("monthlyMinuteLimit value:", salesperson.monthlyMinuteLimit);
      console.log(
        "Type of monthlyMinuteLimit:",
        typeof salesperson.monthlyMinuteLimit
      );
      console.log("Is null?", salesperson.monthlyMinuteLimit === null);
      console.log(
        "Is undefined?",
        salesperson.monthlyMinuteLimit === undefined
      );

      setFormData({
        firstName: salesperson.firstName || "",
        lastName: salesperson.lastName || "",
        dateOfBirth: salesperson.dateOfBirth || "",
        employmentDate: salesperson.employmentDate || "",
        description: salesperson.description || "",
      });

      // Initialize limit - check if it exists and is not null/undefined
      if (
        salesperson.monthlyMinuteLimit !== null &&
        salesperson.monthlyMinuteLimit !== undefined
      ) {
        console.log("✅ HAS LIMIT - Setting toggle to TRUE");
        console.log("Limit in seconds:", salesperson.monthlyMinuteLimit);
        const minutesValue = Math.floor(salesperson.monthlyMinuteLimit / 60);
        console.log("Limit in minutes:", minutesValue);

        setHasLimit(true);
        setMonthlyMinuteLimit(minutesValue.toString());
      } else {
        console.log("❌ NO LIMIT - Setting toggle to FALSE");
        setHasLimit(false);
        setMonthlyMinuteLimit("");
      }
    } else {
      console.log("No salesperson object provided");
    }

    console.log("=== End useEffect ===");
  }, [salesperson]);

  // Load login info when modal opens

  const loadLoginInfo = useCallback(async () => {
    setLoadingLoginInfo(true);
    try {
      const response = await apiService.getSalespersonLoginInfo(salesperson.id);
      if (response.success && response.data) {
        setLoginInfo(response.data);
        if (response.data.hasLogin && response.data.email) {
          setLoginEmail(response.data.email);
          setOriginalLoginEmail(response.data.email);
        }
        if (response.data.hasLogin && response.data.userType) {
          setLoginUserType(response.data.userType);
          setOriginalLoginUserType(response.data.userType);
        }
      }
    } catch (error) {
      console.error("Failed to load login info:", error);
    } finally {
      setLoadingLoginInfo(false);
    }
  }, [salesperson.id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  useEffect(() => {
    if (salesperson && isOpen) {
      loadLoginInfo();
    }
  }, [salesperson, isOpen, loadLoginInfo]);

  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return "Password must contain at least one uppercase letter";
    }
    if (!/(?=.*\d)/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/(?=.*[@$!%*?&])/.test(password)) {
      return "Password must contain at least one special character (@$!%*?&)";
    }
    return null;
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Profile validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length > 100) {
      newErrors.firstName = "First name must be less than 100 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length > 100) {
      newErrors.lastName = "Last name must be less than 100 characters";
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = "Description must be less than 1000 characters";
    }

    if (formData.dateOfBirth && new Date(formData.dateOfBirth) > new Date()) {
      newErrors.dateOfBirth = "Date of birth cannot be in the future";
    }

    if (
      formData.employmentDate &&
      new Date(formData.employmentDate) > new Date()
    ) {
      newErrors.employmentDate = "Employment date cannot be in the future";
    }

    // Login validation (if applicable)
    if (showCreateLogin || loginInfo?.hasLogin) {
      if (loginEmail.trim()) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
          newErrors.loginEmail = "Please enter a valid email";
        }
      } else if (showCreateLogin) {
        newErrors.loginEmail = "Email is required";
      }

      if (loginPassword) {
        const passwordError = validatePassword(loginPassword);
        if (passwordError) {
          newErrors.loginPassword = passwordError;
        }

        // ADD THIS: Check password confirmation
        if (loginPasswordConfirm !== loginPassword) {
          newErrors.loginPasswordConfirm = "Passwords do not match";
        }
      } else if (showCreateLogin) {
        newErrors.loginPassword = "Password is required";
      }

      // ADD THIS: Check confirm password is provided
      if (loginPassword && !loginPasswordConfirm) {
        newErrors.loginPasswordConfirm = "Please confirm your password";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    let profileSuccess = false;
    let loginSuccess = false;

    try {
      // Step 1: Always update profile
      const submitData = {
        ...formData,
        dateOfBirth: formData.dateOfBirth || undefined,
        employmentDate: formData.employmentDate || undefined,
        description: formData.description || undefined,
        monthlyMinuteLimit:
          hasLimit && monthlyMinuteLimit
            ? parseInt(monthlyMinuteLimit) * 60
            : null, // NEW: convert minutes to seconds
      };

      const profileResponse = await apiService.updateSalesperson(
        salesperson.id,
        submitData
      );

      if (profileResponse.success) {
        profileSuccess = true;
      } else {
        toast.error(profileResponse.message || "Failed to update employee");
        setLoading(false);
        return;
      }

      // Step 2: Handle login operations
      if (loginInfo?.hasLogin) {
        // Login exists - check if email, password, or userType changed
        const emailChanged = loginEmail !== originalLoginEmail;
        const passwordChanged = loginPassword.trim() !== "";
        const userTypeChanged = loginUserType !== originalLoginUserType;

        if (emailChanged || passwordChanged || userTypeChanged) {
          const updateData: { email?: string; password?: string; userType?: "manager" | "salesperson" } = {};
          if (emailChanged) updateData.email = loginEmail;
          if (passwordChanged) updateData.password = loginPassword;
          if (userTypeChanged) updateData.userType = loginUserType;

          const loginResponse = await apiService.updateSalespersonLogin(
            salesperson.id,
            updateData
          );

          if (loginResponse.success) {
            loginSuccess = true;
          } else {
            toast.error("Profile updated, but login update failed");
          }
        }
      } else if (showCreateLogin) {
        // No login exists - create one
        const loginResponse = await apiService.createSalespersonLogin(
          salesperson.id,
          { email: loginEmail, password: loginPassword, userType: loginUserType }
        );

        if (loginResponse.success) {
          loginSuccess = true;
        } else {
          toast.error("Profile updated, but login creation failed");
        }
      }

      // Success messages
      if (profileSuccess && loginSuccess) {
        toast.success("Employee updated successfully!");
      } else if (profileSuccess && showCreateLogin) {
        toast.success("Employee updated created successfully!");
      } else if (profileSuccess) {
        toast.success("Employee updated successfully!");
      }

      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error("Update error:", error);
      const message =
        error.response?.data?.message || "Failed to update employee";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLogin = async () => {
    setLoading(true);
    try {
      const response = await apiService.deleteSalespersonLogin(salesperson.id);

      if (response.success) {
        toast.success("Login deleted successfully!");
        setShowDeleteLoginConfirm(false);
        setLoginEmail("");
        setLoginPassword("");
        setOriginalLoginEmail("");
        setLoginUserType("salesperson");
        setOriginalLoginUserType("salesperson");
        await loadLoginInfo();
      } else {
        toast.error(response.message || "Failed to delete login");
      }
    } catch (error: any) {
      console.error("Delete login error:", error);
      toast.error(error.response?.data?.message || "Failed to delete login");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setErrors({});
      setShowCreateLogin(false);
      setLoginPassword("");
      setLoginPasswordConfirm("");
      setLoginUserType("salesperson");
      setOriginalLoginUserType("salesperson");
      onClose();
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-gray-900"
                  >
                    Edit Employee
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Update information for {salesperson.firstName}{" "}
                      {salesperson.lastName}
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* First Name and Last Name */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        id="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.firstName
                            ? "border-red-300"
                            : "border-gray-300"
                        } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                        placeholder="First name"
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        id="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`mt-1 block w-full rounded-md border ${
                          errors.lastName ? "border-red-300" : "border-gray-300"
                        } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                        placeholder="Last name"
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <label
                      htmlFor="dateOfBirth"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      id="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.dateOfBirth
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                    />
                    {errors.dateOfBirth && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div>

                  {/* Employment Date */}
                  <div>
                    <label
                      htmlFor="employmentDate"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Employment Date
                    </label>
                    <input
                      type="date"
                      name="employmentDate"
                      id="employmentDate"
                      value={formData.employmentDate}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.employmentDate
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                    />
                    {errors.employmentDate && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.employmentDate}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      className={`mt-1 block w-full rounded-md border ${
                        errors.description
                          ? "border-red-300"
                          : "border-gray-300"
                      } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                      placeholder="Additional notes about the employee..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  {/* Monthly Limit */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Monthly Upload Limit
                        </h4>
                        <p className="text-xs text-gray-500">
                          Restrict how many minutes this salesperson can upload
                          per month
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setHasLimit(!hasLimit);
                          if (!hasLimit) {
                            setMonthlyMinuteLimit("");
                          }
                        }}
                        className={`${
                          hasLimit ? "bg-indigo-600" : "bg-gray-200"
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                      >
                        <span
                          className={`${
                            hasLimit ? "translate-x-5" : "translate-x-0"
                          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                      </button>
                    </div>

                    {hasLimit && (
                      <div className="pl-4 border-l-2 border-indigo-200">
                        <label
                          htmlFor="monthlyMinuteLimit"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Monthly Limit (minutes)
                        </label>
                        <input
                          type="number"
                          name="monthlyMinuteLimit"
                          id="monthlyMinuteLimit"
                          min="1"
                          value={monthlyMinuteLimit}
                          onChange={(e) =>
                            setMonthlyMinuteLimit(e.target.value)
                          }
                          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
                          placeholder="e.g., 1000"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Set to control this salesperson's monthly upload
                          capacity.
                        </p>
                        {salesperson.secondsCurrentPeriod &&
                          hasLimit &&
                          monthlyMinuteLimit &&
                          salesperson.secondsCurrentPeriod / 60 >
                            parseInt(monthlyMinuteLimit) && (
                            <p className="mt-1 text-xs text-orange-600">
                              ⚠️ Current usage (
                              {Math.floor(
                                salesperson.secondsCurrentPeriod / 60
                              )}{" "}
                              min) exceeds this limit
                            </p>
                          )}
                      </div>
                    )}
                  </div>

                  {/* Login Management Section */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Login Account
                      </h4>

                      {loadingLoginInfo ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        </div>
                      ) : loginInfo?.hasLogin ? (
                        <div className="space-y-4">
                          {/* Login Status Badge */}
                          <div className="bg-green-50 border border-green-200 rounded-md p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-green-800">
                                  Login Active
                                </p>
                                <p className="text-xs text-green-600 mt-1">
                                  {originalLoginEmail}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => setShowDeleteLoginConfirm(true)}
                                className="text-xs px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded font-medium"
                              >
                                Delete Login
                              </button>
                            </div>
                          </div>

                          {/* Update Login Fields */}
                          <div className="space-y-3 pl-4 border-l-2 border-indigo-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Access Level
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setLoginUserType("salesperson")}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                    loginUserType === "salesperson"
                                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                  }`}
                                >
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "salesperson" ? "bg-indigo-500" : "bg-gray-300"}`} />
                                  Salesperson
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setLoginUserType("manager")}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                    loginUserType === "manager"
                                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                  }`}
                                >
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "manager" ? "bg-indigo-500" : "bg-gray-300"}`} />
                                  Admin
                                </button>
                              </div>
                              <p className="mt-1 text-xs text-gray-500">
                                {loginUserType === "manager"
                                  ? "Admin can manage employees, view all calls, and configure settings."
                                  : "Salesperson can only view their own calls and upload recordings."}
                              </p>
                            </div>

                            <div>
                              <label
                                htmlFor="loginEmail"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Update Email (optional)
                              </label>
                              <input
                                type="email"
                                name="loginEmail"
                                id="loginEmail"
                                value={loginEmail}
                                onChange={(e) => {
                                  setLoginEmail(e.target.value);
                                  if (errors.loginEmail) {
                                    setErrors((prev) => ({
                                      ...prev,
                                      loginEmail: "",
                                    }));
                                  }
                                }}
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.loginEmail
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                placeholder="email@example.com"
                              />
                              {errors.loginEmail && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.loginEmail}
                                </p>
                              )}
                            </div>

                            <div>
                              <label
                                htmlFor="loginPassword"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Change Password (optional)
                              </label>
                              <input
                                type="password"
                                name="loginPassword"
                                id="loginPassword"
                                value={loginPassword}
                                onChange={(e) => {
                                  setLoginPassword(e.target.value);
                                  if (errors.loginPassword) {
                                    setErrors((prev) => ({
                                      ...prev,
                                      loginPassword: "",
                                    }));
                                  }
                                }}
                                onBlur={() => {
                                  if (loginPassword) {
                                    const passwordError =
                                      validatePassword(loginPassword);
                                    if (passwordError) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPassword: passwordError,
                                      }));
                                    }
                                  }
                                }}
                                className={`mt-1 block w-full rounded-md border ${
                                  errors.loginPassword
                                    ? "border-red-300"
                                    : "border-gray-300"
                                } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                placeholder="Leave blank to keep current password"
                              />
                              {errors.loginPassword && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.loginPassword}
                                </p>
                              )}
                              {!errors.loginPassword && loginPassword && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Leave blank to keep current password
                                </p>
                              )}
                            </div>

                            {loginPassword && (
                              <div>
                                <label
                                  htmlFor="loginPasswordConfirm"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Confirm New Password *
                                </label>
                                <input
                                  type="password"
                                  name="loginPasswordConfirm"
                                  id="loginPasswordConfirm"
                                  value={loginPasswordConfirm}
                                  onChange={(e) => {
                                    setLoginPasswordConfirm(e.target.value);
                                    if (errors.loginPasswordConfirm) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPasswordConfirm: "",
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      loginPasswordConfirm &&
                                      loginPassword !== loginPasswordConfirm
                                    ) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPasswordConfirm:
                                          "Passwords do not match",
                                      }));
                                    }
                                  }}
                                  className={`mt-1 block w-full rounded-md border ${
                                    errors.loginPasswordConfirm
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                  placeholder="••••••••"
                                />
                                {errors.loginPasswordConfirm && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.loginPasswordConfirm}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          {!showCreateLogin ? (
                            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                  No login account
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setShowCreateLogin(true)}
                                  className="text-sm px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 font-medium"
                                >
                                  Create Login
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4 pl-4 border-l-2 border-indigo-200">
                              <div>
                                <label
                                  htmlFor="loginEmail"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Login Email *
                                </label>
                                <input
                                  type="email"
                                  name="loginEmail"
                                  id="loginEmail"
                                  value={loginEmail}
                                  onChange={(e) => {
                                    setLoginEmail(e.target.value);
                                    if (errors.loginEmail) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginEmail: "",
                                      }));
                                    }
                                  }}
                                  className={`mt-1 block w-full rounded-md border ${
                                    errors.loginEmail
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                  placeholder="email@example.com"
                                />
                                {errors.loginEmail && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.loginEmail}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label
                                  htmlFor="loginPassword"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Password *
                                </label>
                                <input
                                  type="password"
                                  name="loginPassword"
                                  id="loginPassword"
                                  value={loginPassword}
                                  onChange={(e) => {
                                    setLoginPassword(e.target.value);
                                    if (errors.loginPassword) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPassword: "",
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    if (loginPassword) {
                                      const passwordError =
                                        validatePassword(loginPassword);
                                      if (passwordError) {
                                        setErrors((prev) => ({
                                          ...prev,
                                          loginPassword: passwordError,
                                        }));
                                      }
                                    }
                                  }}
                                  className={`mt-1 block w-full rounded-md border ${
                                    errors.loginPassword
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                  placeholder="••••••••"
                                />
                                {errors.loginPassword && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.loginPassword}
                                  </p>
                                )}
                                {!errors.loginPassword && (
                                  <p className="mt-1 text-xs text-gray-500">
                                    Must be at least 8 characters with
                                    uppercase, lowercase, number, and special
                                    character
                                  </p>
                                )}
                              </div>

                              <div>
                                <label
                                  htmlFor="loginPasswordConfirm"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Confirm Password *
                                </label>
                                <input
                                  type="password"
                                  name="loginPasswordConfirm"
                                  id="loginPasswordConfirm"
                                  value={loginPasswordConfirm}
                                  onChange={(e) => {
                                    setLoginPasswordConfirm(e.target.value);
                                    if (errors.loginPasswordConfirm) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPasswordConfirm: "",
                                      }));
                                    }
                                  }}
                                  onBlur={() => {
                                    if (
                                      loginPasswordConfirm &&
                                      loginPassword !== loginPasswordConfirm
                                    ) {
                                      setErrors((prev) => ({
                                        ...prev,
                                        loginPasswordConfirm:
                                          "Passwords do not match",
                                      }));
                                    }
                                  }}
                                  className={`mt-1 block w-full rounded-md border ${
                                    errors.loginPasswordConfirm
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } px-3 py-2 placeholder-gray-500 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm`}
                                  placeholder="••••••••"
                                />
                                {errors.loginPasswordConfirm && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.loginPasswordConfirm}
                                  </p>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Access Level
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setLoginUserType("salesperson")}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                      loginUserType === "salesperson"
                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "salesperson" ? "bg-indigo-500" : "bg-gray-300"}`} />
                                    Salesperson
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setLoginUserType("manager")}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                      loginUserType === "manager"
                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                    }`}
                                  >
                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${loginUserType === "manager" ? "bg-indigo-500" : "bg-gray-300"}`} />
                                    Admin
                                  </button>
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                  {loginUserType === "manager"
                                    ? "Admin can manage employees, view all calls, and configure settings."
                                    : "Salesperson can only view their own calls and upload recordings."}
                                </p>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  setShowCreateLogin(false);
                                  setLoginEmail("");
                                  setLoginPassword("");
                                  setLoginPasswordConfirm("");
                                  setLoginUserType("salesperson");
                                  setErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.loginEmail;
                                    delete newErrors.loginPassword;
                                    delete newErrors.loginPasswordConfirm;
                                    return newErrors;
                                  });
                                }}
                                className="text-sm text-gray-600 hover:text-gray-800"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Single Submit Button */}
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={handleClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Updating...
                        </>
                      ) : (
                        "Update Employee"
                      )}
                    </button>
                  </div>
                </form>

                {/* Delete Login Confirmation Dialog */}
                {showDeleteLoginConfirm && (
                  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative p-5 border w-96 shadow-lg rounded-md bg-white">
                      <div className="mt-3 text-center">
                        <h3 className="text-lg font-medium text-gray-900">
                          Delete Login
                        </h3>
                        <div className="mt-2 px-7 py-3">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete the login for this
                            salesperson? They will no longer be able to access
                            the system.
                          </p>
                        </div>
                        <div className="items-center px-4 py-3 space-x-2">
                          <button
                            onClick={handleDeleteLogin}
                            disabled={loading}
                            className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => setShowDeleteLoginConfirm(false)}
                            disabled={loading}
                            className="px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditSalespersonModal;
